<script setup lang="ts">
import { computed, inject, ref, watch } from "vue";
import VoiceOver from "@models/VoiceOver";
import { usePlayerControls } from "@modules/player_controls";
import { formatSecondsAsString } from "@modules/helpers";
import { Action } from "@models/ActionHierarchy";
import { Language } from "@models/Video";
import Icon from "@components/Icon.vue";
import SubtitleIcon from "@components/SubtitleIcon.vue";

const props = defineProps<{
  videoId: number;
  currentActionId: string | null;
  isPlaying: boolean;
  disabled: boolean;
  allowFullScreen: boolean;
  isFullScreen: boolean;
  videoRuntime: number;
  actions: Action[];
  voiceOvers: VoiceOver[];
  currentLanguageCode?: string;
  languages?: Language[];
}>();

const emit = defineEmits<{
  (e: "jump-to-action-id", id: string): void;
  (e: "play"): void;
  (e: "pause"): void;
  (e: "toggle-full-screen"): void;
  (e: "open-about-screen"): void;
  (e: "switch-language", code: string): void;
}>();

const {
  muted,
  toggleMuted,
  videoHasSubtitles,
  subtitlesEnabled,
  toggleSubtitles,
  firstAction,
  currentAction,
  currentActionIndex,
  previousAction,
  nextAction,
  elapsedTime,
  formattedElapsedTime,
  formattedRemainingTime,
  goTo,
} = usePlayerControls(props, emit);

const status = computed(() => {
  if (props.isPlaying) return "playing";
  return currentActionIndex.value === props.actions.length - 1 ? "completed" : "paused";
});

const toggleButtonIcon = computed(() => {
  if (status.value === "completed") {
    return "arrow.counterclockwise:bold";
  }
  if (status.value === "playing") return "pause.fill";
  return "play.fill";
});

const toggleLabel = computed(() => {
  if (status.value === "completed") {
    return "Rewind";
  }
  if (status.value === "playing") return "Pause";
  return "Play";
});

const formattedVideoRuntime = computed(() => formatSecondsAsString(props.videoRuntime));

const languageCode = ref(props.currentLanguageCode);
const sortedLanguages = computed(() =>
  [...(props.languages || [])].sort((a, b) => a.name.localeCompare(b.name)),
);
watch(languageCode, (newLanguageCode) => {
  if (newLanguageCode) emit("switch-language", newLanguageCode);
});
</script>

<template>
  <div
    class="flex h-8 justify-between px-3 pb-2 text-sm text-white drop-shadow-sm xs:h-8 sm:h-12 sm:text-xl md:h-14"
  >
    <div class="flex items-center gap-0">
      <button
        class="flex h-full w-8 items-center justify-center rounded text-4xl outline-none ring-white/20 transition-all hover:scale-125 focus-visible:ring disabled:text-white/30 sm:w-10 md:w-12"
        :disabled="disabled || actions.length === 0"
        :title="toggleLabel"
        @click.stop="isPlaying ? $emit('pause') : $emit('play')"
      >
        <!--
        The play icon is an arrow to the right, so we should visually move it to the right to
        make it appear centered _visually_.
        -->
        <Icon
          :icon="toggleButtonIcon"
          :class="[
            status === 'paused' ? 'relative left-0.5' : '',
            status === 'completed' ? 'w-6 xs:w-7 sm:w-9' : 'w-5 xs:w-6 sm:w-8',
          ]"
          class=""
        />
      </button>

      <button
        class="hidden h-full w-8 items-center justify-center rounded outline-none ring-white/20 transition-all hover:scale-150 focus-visible:ring disabled:text-white/30 max-[290px]:hidden xs:flex sm:w-10 md:w-12"
        title="Previous step"
        :disabled="disabled || !previousAction"
        @click.stop="previousAction && goTo(previousAction)"
      >
        <Icon icon="backward.fill" class="w-3 xs:w-4 sm:w-6" />
      </button>

      <button
        class="hidden h-full w-8 items-center justify-center rounded outline-none ring-white/20 transition-all hover:scale-150 focus-visible:ring disabled:text-white/30 max-[290px]:hidden xs:flex sm:w-10 md:w-12"
        title="Previous step"
        :disabled="disabled || !nextAction"
        @click.stop="nextAction && goTo(nextAction)"
      >
        <Icon icon="forward.fill" class="w-3 xs:w-4 sm:w-6" />
      </button>

      <button
        class="flex h-full w-8 items-center justify-center rounded outline-none ring-white/20 transition-all hover:scale-150 focus-visible:ring disabled:text-white/30 sm:w-10 md:w-12"
        :class="muted ? 'relative -left-0.5' : ''"
        :title="muted ? 'Unmute' : 'Mute'"
        :disabled="disabled"
        @click.stop="toggleMuted"
      >
        <font-awesome-icon :icon="muted ? 'volume-mute' : 'volume-up'" class="w-3 xs:w-4 sm:w-6" />
      </button>

      <div class="flex items-center gap-1 pl-2 text-xs max-[350px]:hidden sm:text-base">
        <div class="text-right max-[380px]:hidden">{{ formattedElapsedTime }}</div>
        <div class="text-white/60 max-[380px]:hidden">/</div>
        <div class="text-white/60 max-[380px]:hidden">{{ formattedVideoRuntime }}</div>
        <div class="min-[380px]:hidden">{{ formattedRemainingTime }}</div>
      </div>
    </div>

    <div class="flex items-center" :languages="sortedLanguages">
      <label
        v-if="sortedLanguages.length > 1"
        class="relative flex h-full w-8 items-center justify-center rounded outline-none ring-white/20 transition-all hover:scale-150 focus-visible:ring disabled:text-white/30 sm:w-10 md:w-12"
        title="Language"
        @click.stop
      >
        <Icon icon="globe" class="w-3 xs:w-3.5 sm:w-5" />
        <select v-model="languageCode" class="absolute inset-0 opacity-0">
          <option v-for="language in sortedLanguages" :key="language.code" :value="language.code">
            {{ language.name }}
          </option>
        </select>
      </label>

      <button
        class="flex h-full w-8 items-center justify-center rounded outline-none ring-white/20 transition-all hover:scale-150 focus-visible:ring disabled:text-white/30 sm:w-10 md:w-12"
        :title="
          videoHasSubtitles
            ? subtitlesEnabled
              ? 'Hide Subtitles/closed captions'
              : 'Show Subtitles/closed captions'
            : 'This video doesn’t have any subtitles'
        "
        :disabled="disabled || !videoHasSubtitles"
        @click.stop="toggleSubtitles"
      >
        <SubtitleIcon :enabled="subtitlesEnabled" class="w-3 xs:w-3.5 sm:w-5" />
      </button>

      <button
        class="flex h-full w-8 items-center justify-center rounded outline-none ring-white/20 transition-all hover:scale-150 focus-visible:ring disabled:text-white/30 max-[210px]:hidden sm:w-10 md:w-12"
        title="About this video"
        :disabled="disabled"
        @click.stop="emit('open-about-screen')"
      >
        <Icon icon="info" class="w-3 xs:w-3.5 sm:w-5" />
      </button>

      <button
        v-if="allowFullScreen"
        class="flex h-full w-8 items-center justify-center rounded outline-none ring-white/20 transition-all hover:scale-150 focus-visible:ring disabled:text-white/30 sm:w-10 md:w-12"
        :title="isFullScreen ? 'Exit full screen' : 'Full screen'"
        :disabled="disabled"
        @click.stop="$emit('toggle-full-screen')"
      >
        <font-awesome-icon :icon="isFullScreen ? 'compress' : 'expand'" class="w-3 xs:w-4 sm:w-6" />
      </button>
    </div>
  </div>
</template>
